import { Website, observedAt } from './LeadFacts.jsx';

export function ExternalLink({ href, children, className = 'text-brand-700 hover:underline' }) {
  return href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </a>
  ) : null;
}

export function LeadContacts({ lead }) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
      <h2 className="font-semibold">Contatos e presença digital</h2>
      <dl className="space-y-3 text-sm">
        <div>
          <dt className="text-slate-500">Segmento informado</dt>
          <dd className="font-medium">{lead.categoryLabel || 'Não informado'}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Telefone comercial</dt>
          <dd className="mt-1">
            {lead.phoneHref ? (
              <a href={lead.phoneHref} className="font-medium text-brand-700 hover:underline">
                {lead.phone}
              </a>
            ) : (
              lead.phone || 'Não encontrado na fonte'
            )}
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">E-mail comercial</dt>
          <dd>
            {lead.emailHref ? (
              <a href={lead.emailHref} className="text-brand-700 hover:underline break-all">
                {lead.email}
              </a>
            ) : (
              lead.email || 'Não encontrado na base consultada'
            )}
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">Site ou perfil informado pela fonte</dt>
          <dd className="mt-1">
            <Website lead={lead} />
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">WhatsApp</dt>
          <dd>
            {lead.whatsapp ? (
              <ExternalLink href={lead.whatsapp.sourceUrl}>
                Publicado: {lead.whatsapp.value} — conferir fonte
              </ExternalLink>
            ) : (
              'Não confirmado'
            )}
          </dd>
        </div>
      </dl>
      <div className="flex flex-wrap gap-2">
        {lead.phoneHref && (
          <a className="btn-primary" href={lead.phoneHref}>
            Ligar
          </a>
        )}
        {lead.smsHref && (
          <a className="btn-secondary" href={lead.smsHref}>
            Enviar SMS
          </a>
        )}
        {lead.emailHref && (
          <a className="btn-secondary" href={lead.emailHref}>
            Escrever e-mail
          </a>
        )}
      </div>
      <p className="text-xs text-slate-500">
        Os links abrem seu aplicativo de telefone ou mensagens. Nada é enviado automaticamente. Um
        telefone publicado não confirma uma conta WhatsApp.
      </p>
    </section>
  );
}

export function LeadLocation({ lead }) {
  const location = lead.location ?? {};
  return (
    <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="p-5 space-y-3">
        <h2 className="font-semibold">Localização</h2>
        <p className="text-sm">{lead.address || 'Endereço não informado na fonte'}</p>
        {lead.locationWarning && (
          <p role="note" className="bg-amber-50 rounded-lg p-3 text-sm text-amber-900">
            {lead.locationWarning}
          </p>
        )}
        <div className="flex flex-wrap gap-2 text-sm">
          <ExternalLink href={location.mapsUrl} className="btn-secondary">
            Abrir Google Maps
          </ExternalLink>
          <ExternalLink href={location.directionsUrl} className="btn-secondary">
            Como chegar
          </ExternalLink>
          <ExternalLink href={location.streetViewUrl} className="btn-secondary">
            Abrir Street View
          </ExternalLink>
        </div>
      </div>
      {location.embedUrl ? (
        <iframe
          title={`Mapa da localização de ${lead.canonical_name}`}
          src={location.embedUrl}
          className="w-full h-80 border-0"
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      ) : (
        <p className="px-5 pb-5 text-sm text-slate-500">
          Sem coordenadas registradas para exibir o mapa.
        </p>
      )}
      <div className="p-5 border-t border-slate-100 text-xs text-slate-500 space-y-1">
        {location.latitude !== null && location.latitude !== undefined && (
          <p>
            Coordenadas da fonte: {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}.
          </p>
        )}
        <p>
          Localização: Google Maps. Street View abre a imagem disponível mais próxima; pode não
          haver cobertura no ponto exato.
        </p>
        <p>Se o mapa não carregar, use “Abrir Google Maps”.</p>
        {lead.attributions?.map((source, index) => (
          <p key={index}>
            <ExternalLink href={source.url}>{source.provider}</ExternalLink>
            {!source.url && source.provider}
          </p>
        ))}
      </div>
    </section>
  );
}

export function LeadWebsiteResearch({ lead }) {
  const pages = (lead.websites ?? []).flatMap((website) => website.pages ?? []);
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
      <h2 className="font-semibold">Pesquisa no site</h2>
      {pages.length ? (
        <ul className="space-y-4">
          {pages.map((page, i) => (
            <li key={i} className="text-sm space-y-1">
              <ExternalLink href={page.url}>{page.title || page.url}</ExternalLink>
              <p className="text-slate-600">
                {page.description || page.excerpt || 'Sem resumo salvo.'}
              </p>
              <p className="text-xs text-slate-500">Consultado em {observedAt(page.fetchedAt)}</p>
            </li>
          ))}
        </ul>
      ) : (
        <>
          <span className="inline-flex rounded bg-slate-100 px-2 py-1 text-xs text-slate-600">
            Ainda não consultado
          </span>
          <p className="text-sm text-slate-600">
            {lead.websiteUrl
              ? 'O site ou perfil foi informado pela fonte. O conteúdo das páginas ainda não foi consultado pelo sistema.'
              : 'A fonte não informou um site. Isso não significa que a empresa não tenha um.'}
          </p>
        </>
      )}
    </section>
  );
}

export function LeadRegistry({ lead }) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-semibold">Dados por CNPJ</h2>
        <span className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-600">Em breve</span>
      </div>
      <p className="text-sm">
        <strong>CNPJ:</strong> {lead.registryLookup?.cnpj || 'Não informado na base atual'}
      </p>
      <p className="text-sm text-slate-600">
        Espaço reservado para consultar dados cadastrais e comerciais em fontes como Serasa ou
        outros provedores que seu tenant configurar.
      </p>
      <button className="btn-secondary" type="button" disabled aria-describedby="registry-future">
        Consultar dados por CNPJ
      </button>
      <p id="registry-future" className="text-xs text-slate-500">
        Integração futura. Nenhuma consulta é executada ou cobrada nesta tela.
      </p>
    </section>
  );
}
